import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { WorkSchedulesContext } from "../../providers/work_schedules.provider";

const makeTimeOptions = (stepMins = 15) => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += stepMins) {
      opts.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return opts;
};

const timeOptions = makeTimeOptions(15);
const hhmmToLabel = (hhmm) => moment(hhmm, "HH:mm").format("h:mma");

const initials = (name) => {
  const parts = String(name || "").trim().split(/\s+/);
  const a = parts[0]?.[0] || "";
  const b = parts[1]?.[0] || parts[0]?.[1] || "";
  return (a + b).toUpperCase();
};

const computeEndTime = (startTime, durationMins) => {
  if (!startTime) return "";
  const dur = Number(durationMins || 0);
  if (!dur) return "";

  const [h, m] = String(startTime).split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return "";

  const d = new Date();
  d.setHours(h, m, 0, 0);
  d.setMinutes(d.getMinutes() + dur);

  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const dayLabelFromOffset = (offset) => {
  const d = new Date();
  d.setDate(d.getDate() + (offset || 0));
  if ((offset || 0) === 0) return "Today";
  if ((offset || 0) === 1) return "Tomorrow";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};

const dayISOFromOffset = (offset) => {
  const d = new Date();
  d.setDate(d.getDate() + (offset || 0));
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function AssigneePickerModal({
  open,
  onClose,
  onApply,

  assignees = [],
  getSchedule,
  loadingSchedule = false,
  loadingExistingSchedule = false,
  hasExistingSchedule = false,

  assignPickerDayOffset,
  setAssignPickerDayOffset,

  assignDraft,
  setAssignDraft,

  focusedAssignee,
  setFocusedAssignee,

  assignScheduleStartDate,
  setAssignScheduleStartDate,
  assignScheduleStartTime,
  setAssignScheduleStartTime,
  assignScheduleDuration,
  setAssignScheduleDuration,

  availability = [],
  workOrderId = null,
}) {
  const modalRef = useRef(null);
  const workSchedulesCtx = useContext(WorkSchedulesContext);
  const holidaysFetchedRef = useRef(false);

  const [q, setQ] = useState("");

  const selectedValues = useMemo(() => {
    return Array.isArray(assignDraft) ? assignDraft : [];
  }, [assignDraft]);

  const isSelected = (value) => selectedValues.includes(value);

  // Close on ESC key
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Stop UIkit offcanvas focus-trap from stealing focus away from this modal
  useEffect(() => {
    if (!open) return;
    const el = modalRef.current;
    if (!el) return;
    const stop = (e) => e.stopPropagation();
    el.addEventListener("focusin", stop, true);
    el.addEventListener("focusout", stop, true);
    el.addEventListener("focus", stop, true);
    return () => {
      el.removeEventListener("focusin", stop, true);
      el.removeEventListener("focusout", stop, true);
      el.removeEventListener("focus", stop, true);
    };
  }, [open]);

  // Sync local state when opened and fetch holidays (only once)
  useEffect(() => {
    if (!open) return;
    setQ("");
    
    // Fetch holidays for US only once (±1 year by default on backend)
    if (workSchedulesCtx && workSchedulesCtx.getHolidays && !holidaysFetchedRef.current) {
      holidaysFetchedRef.current = true;
      workSchedulesCtx.getHolidays('US');
    }
  }, [open, workSchedulesCtx]);

  const effectiveDayLabel = useMemo(() => {
    if (!assignScheduleStartDate) return "Today";
    const d = new Date(assignScheduleStartDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (d.getTime() === today.getTime()) return "Today";
    if (d.getTime() === tomorrow.getTime()) return "Tomorrow";
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  }, [assignScheduleStartDate]);

  // Use assignScheduleStartDate directly as the effective date
  const effectiveDayISO = assignScheduleStartDate || dayISOFromOffset(0);

  const isHoliday = useMemo(() => {
    const holidays = workSchedulesCtx?.holidays || [];
    return holidays.some(h => h.holiday_date === effectiveDayISO);
  }, [effectiveDayISO, workSchedulesCtx?.holidays]);

  const holidayInfo = useMemo(() => {
    const holidays = workSchedulesCtx?.holidays || [];
    return holidays.find(h => h.holiday_date === effectiveDayISO);
  }, [effectiveDayISO, workSchedulesCtx?.holidays]);

  const options = useMemo(() => assignees || [], [assignees]);

  const visible = useMemo(() => {
    const term = String(q || "").trim().toLowerCase();
    const filtered = !term 
      ? options 
      : options.filter((a) => String(a.label || "").toLowerCase().includes(term));
    
    // Sort: Staff (recipients) first, then contacts
    return filtered.sort((a, b) => {
      const aIsStaff = a.group === 'Staff';
      const bIsStaff = b.group === 'Staff';
      if (aIsStaff && !bIsStaff) return -1;
      if (!aIsStaff && bIsStaff) return 1;
      return 0;
    });
  }, [q, options]);

  const goPrev = () => {
    const currentDate = new Date(effectiveDayISO + 'T00:00:00');
    currentDate.setDate(currentDate.getDate() - 1);
    const newDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
    setAssignScheduleStartDate?.(newDate);
  };

  const goNext = () => {
    const currentDate = new Date(effectiveDayISO + 'T00:00:00');
    currentDate.setDate(currentDate.getDate() + 1);
    const newDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
    setAssignScheduleStartDate?.(newDate);
  };

  const selectedAssignee = useMemo(() => {
    if (focusedAssignee) return focusedAssignee;
    if (!selectedValues.length) return null;
    return options.find((o) => o.value === selectedValues[0]) || null;
  }, [focusedAssignee, selectedValues, options]);

  const scheduleFor = (assignee) => {
    if (!assignee) {
      return {
        status: 'unknown',
        availability: [],
        busy: [],
        unscheduledCount: 0,
        unscheduledPreview: '',
        timeOff: [],
      };
    }

    // getSchedule now returns structured data directly
    const data = getSchedule?.(assignee, effectiveDayISO);
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return {
        status: 'unknown',
        availability: [],
        busy: [],
        unscheduledCount: 0,
        unscheduledPreview: '',
        timeOff: [],
      };
    }
    return data;
  };

  const toggleAssignee = (a, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    setAssignDraft?.((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const exists = arr.includes(a.value);
      return exists ? arr.filter((v) => v !== a.value) : [...arr, a.value];
    });

    setFocusedAssignee?.(a);
    // Don't override the date if there's already a schedule
    // Only set it when toggling assignees for new work orders

    if (!assignScheduleStartTime) {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, "0");
      // Snap to next 15-minute increment
      const rawMm = now.getMinutes();
      const snapped = Math.ceil(rawMm / 15) * 15;
      const mm = snapped >= 60 ? "00" : String(snapped).padStart(2, "0");
      const finalHh = snapped >= 60 ? String((now.getHours() + 1) % 24).padStart(2, "0") : hh;
      setAssignScheduleStartTime?.(`${finalHh}:${mm}`);
    }

    if (!assignScheduleDuration) setAssignScheduleDuration?.("60");
  };

  const clear = () => {
    setAssignDraft?.([]);
    setFocusedAssignee?.(null);
  };

  const computedEnd = useMemo(
    () => computeEndTime(assignScheduleStartTime, assignScheduleDuration),
    [assignScheduleStartTime, assignScheduleDuration]
  );

  // Reactive overlap conflict detection
  const overlapConflicts = useMemo(() => {
    if (!assignScheduleStartDate || !assignScheduleStartTime || !computedEnd) return [];
    if (!selectedValues.length || !availability.length) return [];

    const proposedStart = assignScheduleStartTime;
    const proposedEnd = computedEnd;
    const proposedDate = assignScheduleStartDate || effectiveDayISO;
    const currentWoId = workOrderId ? String(workOrderId) : null;

    const conflicts = [];

    for (const val of selectedValues) {
      const assigneeAvail = availability.find(
        (a) => `${a.assignee_type}_${a.assignee_id}` === val
      );
      if (!assigneeAvail) continue;

      const dateAvail = assigneeAvail.availability?.find(
        (d) => d.date === proposedDate
      );
      if (!dateAvail || !dateAvail.engagements) continue;

      for (const eng of dateAvail.engagements) {
        if (eng.type !== 'work_order') continue;
        if (currentWoId && String(eng.work_order_id) === currentWoId) continue;

        const engStart = String(eng.start_time || "").slice(0, 5);
        const engEnd = String(eng.end_time || "").slice(0, 5);
        if (!engStart || !engEnd) continue;

        if (proposedStart < engEnd && proposedEnd > engStart) {
          conflicts.push({
            name: assigneeAvail.name || val,
            title: eng.title || 'Work order',
            time: `${engStart}–${engEnd}`,
          });
        }
      }
    }

    return conflicts;
  }, [selectedValues, assignScheduleStartDate, assignScheduleStartTime, computedEnd, availability, effectiveDayISO, workOrderId]);

  const hasConflicts = overlapConflicts.length > 0;

  const apply = () => {
    onApply?.();
  };

  if (!open) return null;

  return createPortal(
    <div
      ref={modalRef}
      id="assign-picker-modal"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.5)",
        overflow: "auto",
      }}
      onClick={(e) => {
        // Only close if clicking the backdrop itself (not the dialog)
        if (e.target === modalRef.current) {
          // bgClose disabled — do nothing
        }
      }}
    >
      <div
        style={{
          width: "1100px",
          maxWidth: "95vw",
          background: "#fff",
          borderRadius: 4,
          padding: 30,
          minHeight: "90vh",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 5px 40px rgba(0,0,0,0.3)",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="uk-flex uk-flex-between uk-flex-middle">
          <h2 className="modal-title">Select a Team Member</h2>
          <button
            className="uk-modal-close-default"
            type="button"
            onClick={onClose}
            uk-close="true"
          />
        </div>

        <div>
          <div className="uk-grid-small uk-flex-middle" uk-grid="true" style={{ gap: "20px" }}>
            <div className="uk-width-expand">
              <input
                className="uk-input"
                placeholder="Staff or Contact Name"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>

            <div className="uk-width-auto">
              <div className="uk-flex uk-flex-middle" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="uk-button uk-button-default"
                  disabled={false}
                  onClick={goPrev}
                >
                  ◀
                </button>

                <div 
                  className="uk-text-bold" 
                  style={{ 
                    minWidth: 160, 
                    textAlign: "center",
                    ...(isHoliday ? {
                      backgroundColor: "#ffd700",
                      padding: "8px 12px",
                      borderRadius: "4px",
                      color: "#333"
                    } : {})
                  }}
                  {...(isHoliday && holidayInfo ? { "uk-tooltip": `title: ${holidayInfo.name}; pos: bottom` } : {})}
                >
                  {effectiveDayLabel}
                </div>

                <button type="button" className="uk-button uk-button-default" onClick={goNext}>
                  ▶
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Two-column */}
        <div className="uk-grid-small" uk-grid="true">
          {/* LEFT */}
          <div className="uk-width-2-3@s">
            {visible.length === 0 ? (
              <div className="uk-text-center uk-padding uk-text-muted">
                No matches. Try a different search.
              </div>
            ) : (
              <div style={{ maxHeight: "62vh", overflow: "auto" }}>
                <ul className="uk-list uk-margin-remove">
                  {visible.map((a) => {
                    const selected = isSelected(a.value);
                    const grouped = scheduleFor(a);

                    return (
                      <li key={a.value}>
                        <div
                          className="uk-card uk-card-default uk-card-body assign-team"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => toggleAssignee(a, e)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleAssignee(a, e);
                            }
                          }}
                          style={{
                            border: selected ? "1px solid #1e87f0" : "1px solid #e2e9ed",
                            background: selected ? "#f7fbff" : "#fff",
                          }}
                        >
                          <div className="uk-flex uk-flex-between uk-flex-middle">
                            <div className="uk-flex uk-flex-middle" style={{ gap: 10, minWidth: 0 }}>
                              <div
                                className="uk-border-circle uk-flex uk-flex-center uk-flex-middle"
                                style={{
                                  width: 40,
                                  height: 40,
                                  background: "#1e87f0",
                                  color: "#fff",
                                  fontWeight: 700,
                                  flexShrink: 0,
                                }}
                              >
                                {initials(a.label)}
                              </div>

                              <div style={{ minWidth: 0 }}>
                                <div className="uk-text-bold uk-text-truncate">{a.label}</div>
                                <div className="uk-text-meta">
                                  Schedule for {effectiveDayLabel}
                                </div>
                              </div>
                            </div>

                            <span className="uk-label uk-label-default">{a.group || "\u2014"}</span>
                          </div>

                          <div className="uk-margin-small-top" style={{ borderTop: "1px solid #eee", paddingTop: 10 }}>
                            {loadingSchedule ? (
                              <div className="uk-text-small uk-text-muted" style={{ display: "flex", gap: 8 }}>
                                <span uk-spinner="ratio: 0.5"></span>
                                <span>Loading schedule...</span>
                              </div>
                            ) : (
                              <>
                                {(() => {
                                  const availCount = grouped.availability.length;
                                  const busyCount = grouped.busy.length;
                                  const unsCount = grouped.unscheduledCount || 0;
                                  const timeOffCount = grouped.timeOff.length;
                                  const isOff = grouped.status === 'off';
                                  const isNoData = grouped.status === 'no_data';

                                  return (
                                    <>
                                      {/* Top summary line */}
                                      <div className="uk-text-small uk-margin-small-bottom">
                                        {isNoData ? (
                                          <span className="uk-label uk-label-default">No schedule data</span>
                                        ) : isOff ? (
                                          <span className="uk-label uk-label-warning">Off</span>
                                        ) : availCount > 0 ? (
                                          <span className="uk-label uk-label-success">Available</span>
                                        ) : (
                                          <span className="uk-label uk-label-warning">No availability listed</span>
                                        )}
                                        {!isNoData && (busyCount > 0 || unsCount > 0 || timeOffCount > 0) && (
                                          <span className="uk-text-small uk-text-muted" style={{ marginLeft: 8 }}>
                                            {busyCount > 0 && `${busyCount} scheduled`}
                                            {busyCount > 0 && (unsCount > 0 || timeOffCount > 0) && " • "}
                                            {unsCount > 0 && `${unsCount} unscheduled`}
                                            {unsCount > 0 && timeOffCount > 0 && " • "}
                                            {timeOffCount > 0 && "Time off"}
                                          </span>
                                        )}
                                      </div>

                                      {/* Time off section */}
                                      {timeOffCount > 0 && (
                                        <div className="uk-margin-small-bottom">
                                          {grouped.timeOff.map((s, idx) => (
                                            <div key={`off-${idx}`} className="uk-margin-xsmall-bottom">
                                              <span className="uk-text-truncate">Off all day{s.desc && s.desc.toLowerCase() !== 'off' ? `: ${s.desc}` : ""}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {/* Availability section */}
                                      {availCount > 0 && (
                                        <div className="uk-margin-small-bottom">
                                          <div className="uk-form-label">Availability:</div>
                                          {grouped.availability.map((s, idx) => (
                                            <div key={`a-${idx}`} className="uk-margin-xsmall-bottom" style={{ display: "flex", gap: 6 }}>
                                              <span style={{ width: 18, display: "inline-flex", alignItems: "center" }}>
                                                {String(s.icon || "").startsWith("/images/") ? (
                                                  <img src={s.icon} alt="" />
                                                ) : (
                                                  <span>{s.icon}</span>
                                                )}
                                              </span>
                                              <span style={{ minWidth: 88, fontWeight: 600 }}>{s.time}</span>
                                              <span className="uk-text-truncate" style={{ flex: 1 }}>{s.desc}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {/* Busy / Scheduled section */}
                                      {busyCount > 0 && (
                                        <div className="uk-margin-small-bottom">
                                          <div className="uk-form-label">Scheduled:</div>
                                          {grouped.busy.map((s, idx) => (
                                            <div key={`b-${idx}`} className="uk-margin-xsmall-bottom" style={{ display: "flex", gap: 6 }}>
                                              <span style={{ width: 18, display: "inline-flex", alignItems: "center" }}>
                                                {String(s.icon || "").startsWith("/images/") ? (
                                                  <img src={s.icon} alt="" />
                                                ) : (
                                                  <span>{s.icon || ""}</span>
                                                )}
                                              </span>
                                              <span style={{ minWidth: 88, fontWeight: 600 }}>{s.time}</span>
                                              <span className="uk-text-truncate" style={{ flex: 1 }}>{s.desc}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {/* Unscheduled section */}
                                      {unsCount > 0 && (
                                        <div className="uk-margin-small-bottom">
                                          <div className="uk-text-small uk-text-muted">
                                            {unsCount} unscheduled work order{unsCount !== 1 ? 's' : ''} assigned
                                          </div>
                                        </div>
                                      )}

                                      {/* Empty state */}
                                      {!isNoData && !isOff && availCount === 0 && busyCount === 0 && unsCount === 0 && timeOffCount === 0 && (
                                        <div className="uk-text-small uk-text-muted">No schedule entries</div>
                                      )}
                                    </>
                                  );
                                })()}
                              </>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          {/* RIGHT */}
          <div className="uk-width-1-3@s">
            <div className="uk-card uk-card-default uk-card-body selected-assignees">
              {selectedValues.length === 0 ? (
                <div className="uk-padding-small">
                  Select team members to assign to this work order.
                </div>
              ) : loadingExistingSchedule ? (
                <div className="uk-text-center uk-padding-small uk-text-muted">
                  <div uk-spinner="ratio: 0.8" style={{ marginBottom: 8 }}></div>
                  <div>Loading schedule…</div>
                </div>
              ) : (
                <>
                  <div className="uk-margin-small-bottom">
                    <div className="uk-form-label">Selected Assignees:</div>
                    {selectedValues.map(val => {
                      const assignee = options.find(o => o.value === val);
                      return assignee ? (
                        <div key={val} style={{ marginTop: 4 }}>
                          • {assignee.label}
                        </div>
                      ) : null;
                    })}
                  </div>

                  <hr className="uk-margin-small" />

                  <div className="uk-margin-small">
                    <label className="uk-form-label">Start Date</label>
                    <input
                      className="uk-input"
                      type="date"
                      value={assignScheduleStartDate || effectiveDayISO}
                      onChange={(e) => setAssignScheduleStartDate?.(e.target.value)}
                    />
                  </div>

                  <div className="uk-margin-small">
                    <label className="uk-form-label">Start Time</label>
                    <select
                      className="uk-select"
                      value={assignScheduleStartTime || ""}
                      onChange={(e) => setAssignScheduleStartTime?.(e.target.value)}
                    >
                      <option value="">Select time</option>
                      {timeOptions.map((t) => (
                        <option key={t} value={t}>{hhmmToLabel(t)}</option>
                      ))}
                    </select>
                  </div>

                  <div className="uk-margin-small">
                    <label className="uk-form-label">Estimated Duration</label>
                    <select
                      className="uk-select"
                      value={assignScheduleDuration || "60"}
                      onChange={(e) => setAssignScheduleDuration?.(e.target.value)}
                    >
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="90">1.5 hours</option>
                      <option value="120">2 hours</option>
                      <option value="180">3 hours</option>
                      <option value="240">4 hours</option>
                    </select>
                  </div>
                  <div className="uk-text-muted">Include travel time.</div>

                  {hasConflicts && (
                    <div className="uk-margin-small" style={{ color: "#f0506e" }}>
                      {overlapConflicts.map((c, idx) => (
                        <div key={idx} className="uk-text-small uk-margin-xsmall-bottom">
                          ⚠ {c.name} already has a work order scheduled for the time picked.
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="uk-flex uk-flex-right uk-margin-top">
                    <button type="button" className="uk-button hms-btn hms-white-btn" onClick={clear}>
                      RESET
                    </button>

                    <button type="button" className="uk-button hms-btn uk-offcanvas-close" onClick={onClose}>
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="uk-button hms-btn hms-red-btn"
                      onClick={apply}
                      disabled={selectedValues.length === 0 || hasConflicts}
                    >
                      Assign
                    </button>
                  </div>                  
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
